import axios from 'axios';
import { parseHttpError } from '../utils/httpError';

const API_BASE = process.env.REACT_APP_API_BASE;

/**
 * Authenticator TOTP. Microsoft is otpType 3 and Google is otpType 4.
 * Both use these same endpoints. The chosen otpType is saved on the user after verify.
 *
 * Enrolment (authenticated user, Bearer token):
 *
 * POST {API_BASE}auth/setup-authenticator
 *   Starts TOTP setup. Typical 200 body:
 *   { manualEntryKey, qrCodeUrl } — qrCodeUrl is the otpauth:// URI for the QR code;
 *   manualEntryKey is the Base32 secret for manual entry.
 *
 * POST {API_BASE}auth/update-authenticator
 *   Enable TOTP after setup: { "enable": true, "code": "123456" } (no password).
 *   Disable TOTP: { "enable": false, "password": "...", "code": "..." } (password + current app code required).
 *   After disable, backend falls back to email 2FA.
 *
 * Recovery (lost phone / cannot use authenticator at sign-in):
 * (API_BASE already includes the version segment, e.g. .../v1/)
 * POST {API_BASE}2fa-recovery
 *   Body: { "email": "..." } — sends a link to complete disable (per OpenAPI /v{version}/2fa-recovery).
 * POST {API_BASE}2fa-disable
 *   Body: { "id": "<user id>", "token": "<token>" }.
 *   FE trial: token = encodeURIComponent(URLSearchParams.get('token')) (re-encode after decode).
 *
 * Login MFA (TOTP and others):
 * POST {API_BASE}auth/credential — returns userId, otpType.
 * POST {API_BASE}auth/2fa-verify — second step for all MFA types including authenticator.
 */

function mapBeginEnrollmentResponse(data) {
  if (!data || typeof data !== 'object') {
    return {
      enrollmentId: null,
      otpauthUri: '',
      sharedSecret: '',
      issuer: '',
      accountName: '',
    };
  }
  return {
    enrollmentId: data.enrollmentId ?? data.enrollment_id ?? data.setupToken ?? null,
    otpauthUri:
      data.qrCodeUrl ?? data.otpauthUri ?? data.otpauth_uri ?? data.qrCodePayload ?? data.uri ?? data.setupUri ?? '',
    sharedSecret: data.manualEntryKey ?? data.sharedSecret ?? data.shared_secret ?? data.secret ?? '',
    issuer: data.issuer ?? data.Issuer ?? '',
    accountName: data.accountName ?? data.account_name ?? data.label ?? data.name ?? '',
  };
}

export async function postBeginAuthenticatorEnrollment() {
  const url = `${API_BASE}auth/setup-authenticator`;
  const res = await axios.post(url, {});
  return mapBeginEnrollmentResponse(res.data);
}

export async function postVerifyAuthenticatorEnrollment(payload) {
  const url = `${API_BASE}auth/update-authenticator`;
  const res = await axios.post(url, { enable: true, code: payload.code });
  return res.data;
}

export async function postDisableAuthenticator({ password, code }) {
  const url = `${API_BASE}auth/update-authenticator`;
  const res = await axios.post(url, {
    enable: false,
    password,
    code,
  });
  return res.data;
}

export async function postSendAuthenticatorRecoveryEmail({ email }) {
  const url = `${API_BASE}2fa-recovery`;
  const res = await axios.post(url, { email });
  return res.data;
}

export async function postConfirmAuthenticatorRecovery({ userId, token }) {
  const url = `${API_BASE}2fa-disable`;
  const res = await axios.post(url, { id: userId, token });
  return res.data;
}

export function getAuthenticatorEnrollmentErrorMessage(err) {
  const { message, data } = parseHttpError(err);
  return message || (typeof data === 'string' ? data : 'Request failed');
}
